import {
  ColDef,
  GetContextMenuItemsParams,
  MenuItemDef,
} from '@ag-grid-community/core'
import { AgGridReact } from '@ag-grid-community/react'
import { DownOutlined, RightOutlined } from '@ant-design/icons'
import React, { useState } from 'react'
import { EligibilityData } from '../../types/eligibility-data'
import { CensusFileRowGridData } from '../../types/census-file-row-grid-data'
import ReviewEligibility from './ReviewEligibility'

interface UpdateRecordRowProps {
  row: CensusFileRowGridData
  index: number
  isChecked: boolean
  isExpanded: boolean
  onToggleExpand: (employeeId: string) => void
  onCheckboxChange: (
    e: React.ChangeEvent<HTMLInputElement>,
    row: CensusFileRowGridData
  ) => void
  nameLink: React.ReactNode
  activeBenefits: string
  colDefs: ColDef[]
  rowData: any[]
  getContextMenuItems: (
    params: GetContextMenuItemsParams
  ) => (string | MenuItemDef)[]
  onCellValueChanged: (params: any) => void
  showReviewEligibility: boolean
  eligibilityData: EligibilityData
  onToggleChange: (employeeId: string, checked: boolean) => void
  onDateChange: (employeeId: string, field: string, value: Date | null) => void
  onDoNotCreateEnrollmentChange: (employeeId: string, checked: boolean) => void
  updateMemberUpdates: (employeeId: string, updates: any) => void
}

function UpdateRecordRow({
  row,
  index,
  isChecked,
  isExpanded,
  onToggleExpand,
  onCheckboxChange,
  nameLink,
  activeBenefits,
  colDefs,
  rowData,
  getContextMenuItems,
  onCellValueChanged,
  showReviewEligibility,
  eligibilityData,
  onToggleChange,
  onDateChange,
  onDoNotCreateEnrollmentChange,
  updateMemberUpdates,
}: UpdateRecordRowProps) {
  const [hasEligibilityChanges, setHasEligibilityChanges] = useState(false)

  return (
    <div
      key={row.id}
      className="ag-theme-alpine"
      style={{
        backgroundColor: isChecked
          ? 'var(--SureCo-Medium-Blue, #E0EAF1)'
          : 'white',
        padding: '8px 15px',
        borderBottom: '1px solid #e8e8e8',
        overflow: 'hidden',
        font: 'SF Pro Text',
        width: 'auto',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header — always visible */}
      <div
        key={index}
        style={{ cursor: 'pointer' }}
        onClick={() => onToggleExpand(row.employeeId)}
      >
        {/* First line: checkbox + name + employee ID + chevron */}
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={isChecked}
            onChange={(e) => onCheckboxChange(e, row)}
            onClick={(e) => e.stopPropagation()}
            style={{
              marginRight: '10px',
              backgroundColor: isChecked
                ? 'var(--Components-Checkbox-Global-colorWhite, #FFFFFF)'
                : 'white',
            }}
          />
          <div
            style={{
              fontSize: '16px',
              fontWeight: 'bold',
              display: 'inline',
              padding: '10px',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {nameLink}
          </div>
          <div
            style={{
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#999999',
              display: 'inline',
              padding: '10px',
            }}
          >
            (EmployeeID: {row.employeeId})
          </div>
          <div
            style={{ marginLeft: 'auto', padding: '0 8px', color: '#999999' }}
          >
            {isExpanded ? <DownOutlined /> : <RightOutlined />}
          </div>
        </div>
        {/* Second line: active benefits */}
        {activeBenefits && (
          <div
            style={{
              fontSize: '14px',
              color: '#999999',
              paddingLeft: '36px',
              paddingBottom: '4px',
            }}
          >
            {activeBenefits}
          </div>
        )}
      </div>

      {/* Collapsible body */}
      {isExpanded && (
        <>
          <div style={{ marginTop: '15px' }} />
          <div className="ag-theme-alpine">
            <AgGridReact
              columnDefs={colDefs}
              rowData={rowData}
              domLayout="autoHeight"
              enableCellTextSelection
              ensureDomOrder
              getContextMenuItems={getContextMenuItems}
              onCellValueChanged={onCellValueChanged}
            />
          </div>
          <div style={{ marginBottom: '15px' }} />
          {showReviewEligibility && (
            <ReviewEligibility
              employeeId={row.employeeId}
              onToggleChange={onToggleChange}
              onDateChange={onDateChange}
              onDoNotCreateEnrollmentChange={onDoNotCreateEnrollmentChange}
              eligibilityData={eligibilityData}
              updateMemberUpdates={updateMemberUpdates}
              hasChanges={hasEligibilityChanges}
              setHasChanges={setHasEligibilityChanges}
            />
          )}
        </>
      )}
    </div>
  )
}

export default UpdateRecordRow
